import axios, { AxiosResponse } from 'axios';
import {
  applicationInfo,
  commentData,
  SgrCatalogItem,
  SgrDistributionItem,
} from '../types/interfaces';
import { DASHBOARD_PAGE_SIZE } from '../utils/Constants';
import { secureGet, securePatch, securePost, securePut } from '../utils/HttpUtils';
import { buildApplicationFileUrl } from '../utils/fileStorageUrl';

const apiUrls = {
  applications: '/v1/private/applications/',
  applicationsAll: '/v1/private/applications/all',
  clients: '/v1/private/clients/',
  dashboard: 'v1/private/stats',
};

export { buildApplicationFileUrl };

export type { SgrCatalogItem, SgrDistributionItem };

/** Líneas base (solicitud / crédito máximo) para prellenar el modal multi-SGR. */
export interface MultiSgrBaseProduct {
  product_line_code: string;
  currency_code: string;
  amount: number;
}

export const sendApplication = async (
  cuit: string,
  application: applicationInfo,
  id: string
) => {
  try {
    if (Number(id) > 0) {
      const url = apiUrls.clients + cuit + '/applications/' + id;
      return securePut(url, application);
    } else {
      const url = apiUrls.clients + cuit + '/applications';
      return securePost(url, application);
    }
  } catch (error) {
    console.error('Error sending application:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const updateApplication = async (
  cuit: string,
  commentData: commentData,
  id: string
) => {
  try {
    const url = apiUrls.clients + cuit + '/applications/' + id;
    return securePatch(url, commentData);
  } catch (error) {
    console.error(
      'Error updating application:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const downloadApplicationFile = async (
  cuit: string,
  applicationId: string,
  fileName: string
) => {
  const token = localStorage.getItem('token');
  try {
    const response: AxiosResponse = await axios.get(
      buildApplicationFileUrl(cuit, applicationId, fileName),
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      }
    );
    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      return {
        success: false,
        message: error.response?.data?.message || 'An error occurred',
        status: error.response?.status,
      };
    }
    return {
      success: false,
      message: 'An unexpected error occurred',
    };
  }
};

export const getApplication = async (cuit: string, id: string) => {
  try {
    const url = apiUrls.clients + cuit + '/applications/' + id;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching application:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getApplications = async (
  page: number,
  statuses: string[] = [],
  text: string = '',
  filters?: Record<string, string>
) => {
  try {
    const statusesQueryParam = statuses
      .filter((status) => status.trim() !== '')
      .map((status) => `&status=${status}`)
      .join('');

    let url =
      apiUrls.applications +
      '?size=' +
      DASHBOARD_PAGE_SIZE +
      '&page=' +
      page +
      statusesQueryParam +
      (text ? '&filterText=' + text : '');

    // Agregar filtros adicionales si existen
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.trim() !== '') {
          url += `&${key}=${encodeURIComponent(value)}`;
        }
      });
    }

    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching applications:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getAllApplications = async (
  page: number,
  statuses: string[] = [],
  text: string = ''
) => {
  try {
    const statusesQueryParam = statuses
      .filter((status) => status.trim() !== '')
      .map((status) => `&status=${status}`)
      .join('');

    const url =
      apiUrls.applicationsAll +
      '?size=' +
      DASHBOARD_PAGE_SIZE +
      '&page=' +
      page +
      statusesQueryParam +
      (text ? '&filterText=' + text : '');
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching all applications:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getApplicationsByCuit = async (cuit: string) => {
  try {
    return getApplications(0, [], cuit);
  } catch (error) {
    console.error(
      'Error fetching applications by CUIT:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getAllApplicationsByCuit = async (cuit: string) => {
  try {
    return getAllApplications(0, [], cuit);
  } catch (error) {
    console.error(
      'Error fetching all applications by CUIT:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getApplicationResume = async () => {
  try {
    const url = apiUrls.dashboard;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching application resume:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const downloadSignerDocument = async (
  id: string | undefined,
  cuit: string | undefined,
  fileName: string | undefined
) => {
  const token = localStorage.getItem('token');
  try {
    const response: AxiosResponse = await axios.get(
      buildApplicationFileUrl(cuit ?? '', id ?? '', fileName ?? ''),
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      }
    );
    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      return {
        success: false,
        message: error.response?.data?.message || 'An error occurred',
        status: error.response?.status,
      };
    } else {
      return {
        success: false,
        message: 'An unexpected error occurred',
      };
    }
  }
};

export const deleteSignerDocument = async (
  id: string,
  cuit: string,
  fileName: string | undefined
) => {
  const token = localStorage.getItem('token');
  try {
    const response: AxiosResponse = await axios.delete(
      buildApplicationFileUrl(cuit, id, fileName ?? ''),
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob',
      }
    );
    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      return {
        success: false,
        message: error.response?.data?.message || 'An error occurred',
        status: error.response?.status,
      };
    } else {
      return {
        success: false,
        message: 'An unexpected error occurred',
      };
    }
  }
};

export const deleteApplicationFile = async (
  cuit: string,
  idApplication: string,
  assetFilePath: string,
  fileType?: string
) => {
  const token = localStorage.getItem('token');
  try {
    const url = buildApplicationFileUrl(cuit, idApplication, assetFilePath);
    const response: AxiosResponse = await axios.delete(url, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      data: fileType ? { file_type: fileType } : {},
    });
    return {
      success: true,
      data: response.data,
      status: response.status,
    };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      return {
        success: false,
        message: error.response?.data?.message || 'An error occurred',
        status: error.response?.status,
      };
    } else {
      return {
        success: false,
        message: 'An unexpected error occurred',
      };
    }
  }
};

export const getContracts = async (applicationId: string | undefined) => {
  try {
    const url = apiUrls.applications + applicationId + '/contracts'
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching contracts:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getSgrCatalog = async () => {
  try {
    return secureGet('/v1/private/application/sgr-catalog');
  } catch (error) {
    console.error(
      'Error fetching SGR catalog:',
      JSON.stringify(error, null, 2),
    );
    throw error;
  }
};

export const getFilters = async (referrerType?: 'application' | 'client') => {
  try {
    let url = '/v1/private/application/filters';
    if (referrerType) {
      url += `?referrer_type=${referrerType}`;
    }
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching filters:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getOfficers = async () => {
  try {
    return secureGet('/v1/private/officers');
  } catch (error) {
    console.error('Error fetching officers:', JSON.stringify(error, null, 2));
    throw error;
  }
};

export const getOtherLegalEntitiesFileTypes = async () => {
  try {
    const url = '/v1/private/applications/file-types/other-legal-entities';
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching other legal entities file types:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const getFileTypes = async () => {
  try {
    const url = '/v1/private/applications/file-types';
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching file types:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

/**
 * Modo del flujo de comité que devuelve ms-application (Task 50111).
 * - LEGACY: aprobaciones por rol secuenciales (cliente Cementos).
 * - MEMBER_QUORUM: quórum de usuarios COMITE (cliente Innova).
 * Backends viejos pueden no enviar el campo; el front debe asumir 'LEGACY'.
 */
export type CommitteeApprovalMode = 'LEGACY' | 'MEMBER_QUORUM';

export interface CommitteeApprovalRole {
  code: string;
  label: string;
}

export interface CommitteeQuorumInfo {
  approvalsCount: number;
  minManualQuorum: number;
  autoQuorum: number;
  cycleId: number;
  userAlreadyApproved: boolean;
  canSendToInstrumentation: boolean;
}

export interface CommitteeApprovalResponse {
  roles: CommitteeApprovalRole[];
  hasNext: boolean;
  nextRole?: CommitteeApprovalRole[] | null;
  mode?: CommitteeApprovalMode;
  quorum?: CommitteeQuorumInfo | null;
  pendingStepOrder?: number | null;
  pendingCaseId?: number | null;
}

export interface CommitteeApprovalHistoryItem {
  employeeName: string;
  roleLabel: string;
  statusLabel: string;
  description: string | null;
  updatedAt: string;
  /** Task 50111: ciclo al que pertenece la entrada. Default 1 si el backend no lo envía. */
  cycleId?: number;
}

export const getCommitteeApproval = async (
  cuit: string,
  id: string,
): Promise<CommitteeApprovalResponse> => {
  try {
    const url = apiUrls.clients + cuit + '/applications/' + id + '/committee/approve';
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching committee approval:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

export const postCommitteeApproval = async (cuit: string, id: string, description: string, statusCode: string) => {
  try {
    const url = apiUrls.clients + cuit + '/applications/' + id + '/committee/approve';
    return securePost(url, { description, status_code: statusCode });
  } catch (error) {
    console.error(
      'Error posting committee approval:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

/**
 * Task 50111 (cliente Innova, modo MEMBER_QUORUM): empuja manualmente la solicitud
 * a INS cuando el conteo de aprobaciones del ciclo actual ya alcanzó el mínimo
 * manual (`committeeMinManualQuorum`) pero todavía no el auto (`committeeAutoQuorum`).
 *
 * El front nunca debe disparar el cambio de estado a INS por sí mismo: ese cambio
 * sólo lo hace ms-application al validar el quórum y los permisos del usuario en
 * el ciclo actual. El front se limita a invocar este endpoint y refrescar.
 */
export const postSendCommitteeToInstrumentation = async (
  cuit: string,
  id: string,
) => {
  try {
    const url =
      apiUrls.clients + cuit + '/applications/' + id + '/committee/send-to-instrumentation';
    return securePost(url, {});
  } catch (error) {
    console.error(
      'Error posting send-to-instrumentation:',
      JSON.stringify(error, null, 2),
    );
    throw error;
  }
};

export const getCommitteeApprovalHistory = async (
  cuit: string,
  id: string,
  page: number = 0,
): Promise<CommitteeApprovalHistoryItem[]> => {
  try {
    const url = apiUrls.clients + cuit + '/applications/' + id + '/committee/approval-history?page=' + page;
    return secureGet(url);
  } catch (error) {
    console.error(
      'Error fetching committee approval history:',
      JSON.stringify(error, null, 2)
    );
    throw error;
  }
};

/**
 * Aprobador masivo de comité (Cementos LEGACY MVP).
 *
 * Acciones permitidas. Se mapean en el BFF a status codes upstream
 * (ms-application): APPROVE → APP, REJECT → REJ, REQUEST_CHANGES → PEN.
 *
 * Linaje SDD: Engram #59 (design) + #63/#64 (BFF foundation/core).
 */
export type BulkAction = 'APPROVE' | 'REJECT' | 'REQUEST_CHANGES';

/**
 * Códigos de error por carpeta del bulk de comité.
 *
 * Clasificación según `isRetryable` (consumida en
 * `src/utils/bulkErrorMessages.ts` por `getErrorCopy(errorCode)`):
 *
 *   Recuperables (el front habilita "Reintentar fallidas"):
 *     - LOCK_TIMEOUT      — advisory lock timeout (race entre miembros).
 *     - UPSTREAM_ERROR    — 5xx / timeout axios contra ms-application.
 *     - UNKNOWN           — fallback / error inesperado.
 *
 *   No recuperables (no se reintentan automáticamente):
 *     - STATUS_NOT_COM        — la solicitud ya no está en estado COM.
 *     - FORBIDDEN_ROLE        — el rol del usuario no puede aprobar/declinar.
 *     - NOT_AUTHORIZED_FOR_STEP — el rol no es el siguiente aprobador.
 *     - VALIDATION_ERROR      — payload mal formado, REJ en MEMBER_QUORUM, etc.
 *
 * Mantener en sync con `bff-moor/src/controllers/application/bulk-decision.response.ts`
 * (BulkErrorCode union). Si el BFF agrega un código nuevo, sumarlo acá y en
 * `src/utils/bulkErrorMessages.ts`.
 */
export type BulkErrorCode =
  | 'STATUS_NOT_COM'
  | 'FORBIDDEN_ROLE'
  | 'NOT_AUTHORIZED_FOR_STEP'
  | 'LOCK_TIMEOUT'
  | 'UPSTREAM_ERROR'
  | 'VALIDATION_ERROR'
  | 'UNKNOWN';

/**
 * Item del array `applications` del bulk request.
 *
 * `cuit + id` son obligatorios porque el BFF reusa los endpoints upstream
 * single (`postCommitteeApproval`, `getCreditoMaximo`, `updateApplicationStatus`)
 * que exigen ambos. `companyName` es opcional: el BFF lo devuelve verbatim en
 * la response para que el modal de resultado muestre la razón social sin
 * lookups adicionales.
 */
export interface BulkApplicationItem {
  cuit: string;
  id: number;
  companyName?: string;
}

/**
 * Body del `POST /v1/private/committee/bulk-decision`.
 *
 * Validado server-side en el BFF (Task 4.3 — guard manual, sin class-validator):
 *   - `applications.length` entre 1 y 10 (cap natural = `DASHBOARD_PAGE_SIZE`).
 *   - `description` requerido y no vacío si `action ∈ {REJECT, REQUEST_CHANGES}`.
 *   - Sin `id` duplicados dentro de `applications`.
 */
export interface BulkDecisionRequest {
  action: BulkAction;
  applications: BulkApplicationItem[];
  description?: string;
}

/**
 * Carpeta procesada con éxito. Conserva la identificación del request
 * (`cuit`, `id`, `companyName?`) más el estado final calculado por
 * ms-application:
 *   - `mode`                          — strategy resuelta por el dispatcher.
 *   - `finalStatusCode`               — estado final tras la operación.
 *   - `hasNext`                       — queda un paso pendiente del comité.
 *   - `transitionedToInstrumentation` — si la solicitud pasó a INS.
 *
 * Códigos de status alineados con `APPLICATION_STATUS` en
 * `src/utils/Constants.tsx` (`'FIN'` para finished cuando
 * `isInstrumentationEnabled === false`).
 */
export interface BulkDecisionItemSuccess {
  cuit: string;
  id: number;
  companyName?: string;
  mode: 'LEGACY' | 'MEMBER_QUORUM';
  finalStatusCode: 'APP' | 'PEN' | 'REJ' | 'INS' | 'FIN' | 'APP_FINAL';
  hasNext: boolean;
  transitionedToInstrumentation: boolean;
}

/**
 * Carpeta que falló. `message` trae el texto devuelto por el upstream (vía
 * BFF). `CommitteeBulkResultModal` lo muestra cuando no está vacío; si falta,
 * usa la copy localizada según `errorCode` (`getErrorCopy` en
 * `src/utils/bulkErrorMessages.ts`).
 */
export interface BulkDecisionItemFailure {
  cuit: string;
  id: number;
  companyName?: string;
  errorCode: BulkErrorCode;
  message: string;
}

/**
 * Response particionado del `POST /v1/private/committee/bulk-decision`.
 *
 * El BFF devuelve HTTP 200 incluso ante fallos parciales. El front decide la
 * UX (modal con secciones "Aprobadas (N)" / "No procesadas (M)" subdividida
 * en recuperables vs no recuperables) en base al split.
 */
export interface BulkDecisionResponse {
  succeeded: BulkDecisionItemSuccess[];
  failed: BulkDecisionItemFailure[];
}

/**
 * Aprobador masivo de comité — invoca el endpoint nuevo del BFF
 * (`POST /v1/private/committee/bulk-decision`) que orquesta hasta 10
 * carpetas en paralelo (`p-limit=3` server-side) y devuelve el resultado
 * particionado en `succeeded[]` / `failed[]`.
 *
 * El usuario debe tener uno de `COMITE_AND_INSTRUMENTADOR_ROLES` y el flag
 * `isCommitteeBulkApprovalEnabled` debe estar `true` en config; el BFF
 * gateaa ambas precondiciones (403 si flag OFF, 401 si moorId ausente,
 * 403 si el rol no aplica).
 */
export const postCommitteeBulkDecision = async (
  payload: BulkDecisionRequest,
): Promise<BulkDecisionResponse> => {
  try {
    const url = '/v1/private/committee/bulk-decision';
    const response = await securePost(url, payload);
    if (!response.success) {
      throw new Error(response.message || 'Error en bulk-decision');
    }
    return response.data as BulkDecisionResponse;
  } catch (error) {
    console.error(
      'Error posting committee bulk decision:',
      JSON.stringify(error, null, 2),
    );
    throw error;
  }
};